import { MaterialIcons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Dimensions, Modal, ScrollView, TouchableOpacity, View } from 'react-native';

import { ThemedText } from '@/components/ThemedText';
import AppStyles from '@/constants/Styles';
import { useThemeColor } from '@/hooks/useThemeColor';
import { LeafletingRouteAssignment, RouteSummary } from '@/types/leafleting';
import { authenticatedFetch } from '@/utils/auth';

interface LeafletingInfoProps {
  routeAssignment: LeafletingRouteAssignment;
  routeSummary: RouteSummary | null;
  loading: boolean;
  error: string | null;
  insets: any;
  isUserInAssignment?: boolean;
  onJoinAssignment?: () => void;
  onLeaveAssignment?: () => void;
  joining?: boolean;
  leaving?: boolean;
  onStatusUpdate?: () => void;
}

export default function LeafletingInfo({ 
  routeAssignment,
  routeSummary, 
  loading: _loading, // Unused - data loads in background
  error, 
  insets,
  isUserInAssignment = false,
  onJoinAssignment,
  onLeaveAssignment,
  joining = false,
  leaving = false,
  onStatusUpdate
}: LeafletingInfoProps) {
  // State for completion modal and API call
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Theme-aware colors
  const cardBackground = useThemeColor({}, 'cardBackground' as any);
  const borderColor = useThemeColor({}, 'border' as any);
  const secondaryTextColor = useThemeColor({}, 'secondaryText' as any);
  const progressHigh = useThemeColor({}, 'progressHigh');
  const progressMedium = useThemeColor({}, 'progressMedium');
  const progressLow = useThemeColor({}, 'progressLow');
  const progressAssigned = useThemeColor({}, 'progressAssigned');
  const iconColor = useThemeColor({}, 'icon' as any);
  const modalBackground = useThemeColor({}, 'background' as any);

  // Get status-based color for progress badge
  const getProgressColor = (status: string) => {
    switch (status) {
      case 'completed':
        return progressHigh; // Green
      case 'in_progress':
        return progressMedium; // Orange
      case 'assigned':
        return progressAssigned; // Red
      case 'unassigned':
      case 'not_doing':
      default:
        return progressLow; // Dark grey
    }
  };

  // Calculate progress percentage
  const isCompleted = routeAssignment.status === 'completed';
  const progressPercentage = isCompleted ? 100 : 0;
  const progressColor = getProgressColor(routeAssignment.status);

  // Format status for display
  const formatStatus = (status: string) => {
    return status.replace('_', ' ');
  };

  // Handle completion confirmation
  const handleCompleteAssignment = async () => {
    setCompleting(true);
    try {
      const response = await authenticatedFetch(
        routeAssignment.branch_short_name,
        '/route-assignments/update-status',
        {
          method: 'POST',
          body: JSON.stringify({
            assignment_id: routeAssignment.assignment_id,
            status: 'completed'
          })
        },
        'groundforce'
      );

      if (response.ok) {
        setShowCompletionModal(false);
        // Call the parent component to refresh data
        if (onStatusUpdate) {
          onStatusUpdate();
        }
      } else {
        throw new Error('Failed to update assignment status');
      }
    } catch (error) {
      console.error('Error completing assignment:', error);
      // Error is already handled by authenticatedFetch (shows alert)
    } finally {
      setCompleting(false);
    }
  };
  
  // Render route information (load in background, no loading state shown)
  return (
    <ScrollView style={{ flex: 1, padding: 16 }} contentContainerStyle={{ paddingBottom: insets.bottom }}>
      {/* Show error if present */}
      {error && (
        <View style={{ marginBottom: 16 }}>
          <ThemedText style={AppStyles.errorText}>Error loading route information: {error}</ThemedText>
        </View>
      )}
      {/* Route Information Section with Progress Badge */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <ThemedText style={[AppStyles.engagementName, { flex: 1, marginRight: 8, marginBottom: 0 }]}>
          {routeAssignment.number_within_branch}: {routeAssignment.route_name}
        </ThemedText>
        <View style={{ 
          backgroundColor: progressColor,
          paddingHorizontal: 12,
          paddingVertical: 6,
          borderRadius: 16,
          minWidth: 60,
          alignItems: 'center'
        }}>
          <ThemedText style={{ color: '#ffffff', fontSize: 14, fontWeight: 'bold' }}>
            {progressPercentage}%
          </ThemedText>
        </View>
      </View>
      
      <View style={{ marginVertical: 12 }}>
        {/* Address Count and Status */}
        <View style={{ 
          backgroundColor: cardBackground, 
          padding: 16, 
          borderRadius: 8, 
          marginBottom: 16 
        }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
            <ThemedText style={AppStyles.engagementStats}>Total Addresses</ThemedText>
            <ThemedText style={AppStyles.engagementStats}>{routeAssignment.address_count}</ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
            <ThemedText style={AppStyles.engagementStats}>Status</ThemedText>
            <ThemedText style={AppStyles.engagementStats}>{formatStatus(routeAssignment.status)}</ThemedText>
          </View>
          {routeAssignment.notes && (
            <View style={{ marginTop: 8 }}>
              <ThemedText style={[AppStyles.engagementStats, { marginBottom: 4 }]}>Notes</ThemedText>
              <ThemedText style={{ color: secondaryTextColor, fontSize: 14 }}>
                {routeAssignment.notes}
              </ThemedText>
            </View>
          )}
        </View>
      </View>

      {/* Campaigners Section */}
      <ThemedText style={AppStyles.engagementName}>Campaigners</ThemedText>
      <View style={AppStyles.campaignerList}>
        {routeAssignment.campaigner_names && routeAssignment.campaigner_names.length > 0 ? (
          [...routeAssignment.campaigner_names]
            .filter((name): name is string => name !== null && name !== undefined)
            .sort((a, b) => a.localeCompare(b))
            .map((name: string, index: number) => (
              <ThemedText key={index} style={AppStyles.campaignerItem}>{name}</ThemedText>
            ))
        ) : (
          <ThemedText style={AppStyles.campaignerItem}>No active campaigners</ThemedText>
        )}
      </View>

        {/* Join/Leave Buttons - Only show if route is not completed */}
        {routeAssignment.status !== 'completed' && (
          isUserInAssignment ? (
            <TouchableOpacity
              style={[
                AppStyles.secondaryButton,
                { 
                  marginTop: 20,
                  marginBottom: 16,
                }
              ]}
              onPress={onLeaveAssignment}
              disabled={leaving}
            >
              <ThemedText style={AppStyles.secondaryButtonText}>
                {leaving ? 'Leaving...' : 'Leave Route Assignment'}
              </ThemedText>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={[
                AppStyles.primaryButton,
                { 
                  marginTop: 20,
                  marginBottom: 16,
                }
              ]}
              onPress={onJoinAssignment}
              disabled={joining}
            >
              <ThemedText style={AppStyles.primaryButtonText}>
                {joining ? 'Joining...' : 'Join Route Assignment'}
              </ThemedText>
            </TouchableOpacity>
          )
        )}

        {/* Mark as Complete Button - Only show if user is in assignment and not already completed */}
        {isUserInAssignment && routeAssignment.status !== 'completed' && (
          <TouchableOpacity
            style={[
              AppStyles.primaryButton,
              { 
                marginTop: 8,
                marginBottom: 16,
                backgroundColor: progressHigh,
              }
            ]}
            onPress={() => setShowCompletionModal(true)}
            disabled={completing}
          >
            <ThemedText style={[AppStyles.primaryButtonText, { color: '#ffffff' }]}>
              {completing ? 'Completing...' : 'Mark as Complete'}
            </ThemedText>
          </TouchableOpacity>
        )}

      {/* Street Breakdown Section - Only show if user is assigned */}
      {isUserInAssignment && routeSummary && routeSummary["Street Breakdown"] && routeSummary["Street Breakdown"].length > 0 && (
        <>
          <ThemedText style={AppStyles.engagementName}>Streets</ThemedText>
          <View style={AppStyles.campaignerList}>
            {[...routeSummary["Street Breakdown"]]
                .sort((a, b) => {
                  // Sort by street name first, then by street sub name
                  if (a.street_name !== b.street_name) {
                    return a.street_name.localeCompare(b.street_name);
                  }
                  // If street names are the same, sort by street sub name
                  const aSubName = a.street_sub_name || '';
                  const bSubName = b.street_sub_name || '';
                  return aSubName.localeCompare(bSubName);
                })
                .map((street, index, sortedStreets) => {
                  const screenWidth = Dimensions.get('window').width;
                  const isNarrowScreen = screenWidth < 400;
                  
                  return (
                    <View key={index} style={{ 
                      paddingVertical: 8,
                      borderBottomWidth: index < sortedStreets.length - 1 ? 1 : 0,
                      borderBottomColor: borderColor
                    }}>
                      <View style={{ 
                        flexDirection: isNarrowScreen ? 'column' : 'row', 
                        justifyContent: isNarrowScreen ? 'flex-start' : 'space-between',
                        alignItems: isNarrowScreen ? 'flex-start' : 'center'
                      }}>
                        <ThemedText style={{ 
                          fontSize: 16,
                          marginBottom: isNarrowScreen ? 4 : 0,
                          flex: isNarrowScreen ? 0 : 1
                        }}>
                          {street.street_sub_name ? `${street.street_sub_name}, ${street.street_name}` : street.street_name}
                          {street.postcodes.length > 0 && (
                            <ThemedText> ({street.postcodes.join(', ')})</ThemedText>
                          )}
                        </ThemedText>
                        <ThemedText style={{ 
                          fontSize: 14, 
                          color: secondaryTextColor,
                          alignSelf: isNarrowScreen ? 'flex-start' : 'flex-end'
                        }}>
                          {street.in_scope_address_count} address{street.in_scope_address_count !== 1 ? 'es' : ''}
                        </ThemedText>
                      </View>
                    </View>
                  );
                })}
            </View>
          </>
        )}

      {/* Completion Confirmation Modal */}
      <Modal
        visible={showCompletionModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCompletionModal(false)}
      >
        <TouchableOpacity 
          style={AppStyles.modalContainer} 
          activeOpacity={1} 
          onPress={() => setShowCompletionModal(false)}
        >
          <View style={[AppStyles.modalView, { backgroundColor: modalBackground }]}>
            <TouchableOpacity
              style={AppStyles.modalCloseButton}
              onPress={() => setShowCompletionModal(false)}
              accessibilityLabel="Close dialog"
              accessibilityHint="Closes the completion confirmation dialog"
            >
              <MaterialIcons name="close" size={20} color={iconColor} />
            </TouchableOpacity>
            
            <ThemedText style={AppStyles.modalTitle}>
              Complete Route Assignment
            </ThemedText>
            
            <ThemedText style={{ 
              fontSize: 16, 
              textAlign: 'center', 
              marginBottom: 30,
              color: secondaryTextColor 
            }}>
              Have you delivered all the leaflets in this route?
            </ThemedText>
            
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12 }}>
              <TouchableOpacity
                style={[
                  AppStyles.secondaryButton,
                  { flex: 1, marginBottom: 0 }
                ]}
                onPress={() => setShowCompletionModal(false)}
                disabled={completing}
              >
                <ThemedText style={AppStyles.secondaryButtonText}>
                  No
                </ThemedText>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[
                  AppStyles.primaryButton,
                  { 
                    flex: 1, 
                    marginBottom: 0,
                    backgroundColor: progressHigh,
                  }
                ]}
                onPress={handleCompleteAssignment}
                disabled={completing}
              >
                <ThemedText style={[AppStyles.primaryButtonText, { color: '#ffffff' }]}>
                  {completing ? 'Completing...' : 'Yes'}
                </ThemedText>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </ScrollView>
  );
}
